/**
 * Delivery Settings - Buffer Days Configuration API
 *
 * BufferDaysApi is backed by a single Azure Blob holding the WHOLE list of
 * rules as one JSON array — there is no per-record endpoint. Every add,
 * update or delete means: take the full current array, apply the one
 * change client-side, then send the ENTIRE array back:
 *   - POST once, the very first time the blob doesn't exist yet (create).
 *   - PUT with the full array for every save after that — update, delete,
 *     and any create once at least one record already exists.
 * Each rule gets a client-generated unique id when created, since the blob
 * itself never issues one. The blob only stores whatever fields were
 * actually set — a rule with no product/colour/etc. restrictions can come
 * back as sparse as { bufferDays, updatedBy, notes } — every response is
 * normalized so the rest of the app can keep assuming a fully-populated
 * IBufferDaysRule.
 */

import { NetworkMethodEnum, Obj } from "@/types";
import { fetcher } from "@/utils/api/dataverse/fetcher";
import { mutator } from "@/utils/api/dataverse/mutator";
import { IBufferDaysRule, IProfileColourCombination } from "../types";

export const URL = {
  bufferDays: "/BufferDaysApi",
};

export type ICreateBufferDaysRulePayload = Omit<IBufferDaysRule, "id">;
export type IUpdateBufferDaysRulePayload = IBufferDaysRule;

const asStringArray = (value: unknown): string[] => (Array.isArray(value) ? value.filter((v) => typeof v === "string") : []);
const asNumberArray = (value: unknown): number[] => (Array.isArray(value) ? value.filter((v) => typeof v === "number") : []);
const asString = (value: unknown): string | undefined => (typeof value === "string" ? value : undefined);

const asColourCombinations = (value: unknown): IProfileColourCombination[] =>
  Array.isArray(value)
    ? value
        .filter((v): v is Obj => v && typeof v === "object")
        .map((v) => ({ internalColorId: asString(v.internalColorId), externalColorId: asString(v.externalColorId) }))
    : [];

export const generateBufferDaysRuleId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `rule-${Date.now()}-${Math.random().toString(36).slice(2)}`;

/** Fill in every possibly-missing field so the UI never has to guard against undefined */
const normalizeBufferDaysRule = (raw: Obj, fallbackId: string): IBufferDaysRule => ({
  id: raw?.id ?? raw?.ruleId ?? fallbackId,
  bufferDays: typeof raw?.bufferDays === "number" ? raw.bufferDays : 0,
  regions: asNumberArray(raw?.regions),
  productIds: asStringArray(raw?.productIds),
  profileColourTypeId: asString(raw?.profileColourTypeId),
  colourCombinations: asColourCombinations(raw?.colourCombinations),
  sprayId: asString(raw?.sprayId),
  timberLockId: asString(raw?.timberLockId),
  securityIds: asStringArray(raw?.securityIds),
  updated: raw?.updated,
  updatedBy: raw?.updatedBy,
  deleted: raw?.deleted === true,
  notes: raw?.notes,
});

/** Get the whole list of buffer days configuration rules */
export const getBufferDaysRulesApi = async (): Promise<IBufferDaysRule[]> => {
  const res = await fetcher(URL.bufferDays);
  const list = res?.data ?? res?.value ?? (Array.isArray(res) ? res : res ? [res] : []);
  return (Array.isArray(list) ? list : []).map((raw: Obj, index: number) =>
    normalizeBufferDaysRule(raw, `rule-${index}`),
  );
};

/**
 * Persist the FULL rules array. `hasExistingData` decides the verb — POST
 * only ever happens once, to create the blob when nothing exists yet;
 * every other save (including the one right after that first POST) uses PUT.
 */
export const saveBufferDaysRulesApi = async (
  rules: IBufferDaysRule[],
  hasExistingData: boolean,
): Promise<void> => {
  const method = hasExistingData ? NetworkMethodEnum.PUT : NetworkMethodEnum.POST;
  await mutator(method, URL.bufferDays, { data: rules });
};
